import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"

// Size class shared by parking spots (set by an admin) and cars (set by the user). The assignment
// algorithm matches the two via the preference matrix in services/sizeMatching. Regular is the neutral
// default on both sides, so a company that never sets sizes behaves exactly as before this feature.
export enum ParkingSize {
    Small = 0, // malá - harder to access, better for smaller vehicles
    Regular = 1, // běžná - standard spot / car
    Large = 2, // velká - SUV-friendly, wider
    High = 3 // vysoká - vans 2m+ (van bays)
}

export const allParkingSizes: ParkingSize[] = Object.values(ParkingSize).filter(value => typeof value === "number")

/**
 * Coerces any stored/incoming value to a valid ParkingSize, falling back to Regular.
 * Accepts numeric strings ("2") because web form selects submit strings.
 */
export const normalizeParkingSize = (value: unknown): ParkingSize => {
    const numeric = typeof value === "string" && value.trim() !== "" ? Number(value) : value
    return typeof numeric === "number" && allParkingSizes.includes(numeric) ? (numeric) : ParkingSize.Regular
}

/**
 * Size field for STORED documents - always resolves to a valid size and never rejects.
 * A strict field here would be dangerous: an unexpected value (null, "2", a future size) would fail the
 * whole document parse, so fromData would return null and the spot/car would silently disappear - for a
 * spot that means lost lot capacity and users denied parking. Degrade to Regular instead, and warn.
 */
export const storedParkingSizeField = z.preprocess(value => {
    const normalized = normalizeParkingSize(value)
    if (value !== undefined && value !== null && value !== normalized) {
        logger.warn(`Unexpected parking size ${JSON.stringify(value)}, falling back to Regular (${ParkingSize.Regular})`)
    }
    return normalized
}, z.enum(ParkingSize))

/**
 * Size field for API INPUT (create/update payloads). Tolerant about representation (accepts "2" and a
 * missing value) but strict about validity: a genuinely invalid size is rejected so the caller finds out,
 * rather than being silently downgraded to Regular.
 */
export const inputParkingSizeField = z.preprocess(
    value => (value === undefined || value === null ? ParkingSize.Regular : typeof value === "string" ? Number(value) : value),
    z.enum(ParkingSize)
)

const parkingSpotSchema = z.object({
    name: z.string().min(1),
    electric: z.boolean().default(false),
    imageUrl: z.string().nullish(),
    imageFilename: z.string().nullish(),
    attractivity: z.number().default(0),
    naturalGasAllowed: z.boolean().default(true),
    isEnabled: z.boolean().default(true),
    inReserve: z.boolean().default(false),
    size: storedParkingSizeField,
    timestampCreated: z.custom<Timestamp>().nullish()
})

export type SpotId = string

interface IParkingSpot {
    id: string
    name: string
    electric: boolean
    /** @deprecated Deprecated in 2.0 **/
    imageUrl?: string
    imageFilename?: string
    attractivity: number
    naturalGasAllowed: boolean
    inReserve: boolean
    isEnabled: boolean
    /** Size class of the spot. Optional on the model; unset is treated as Regular by the matcher. */
    size?: ParkingSize
    timestampCreated?: Date
}

export interface ParkingSpot extends IParkingSpot {}
export class ParkingSpot {
    constructor(params: IParkingSpot) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): ParkingSpot | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create ParkingSpot ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = parkingSpotSchema.parse(raw)
            return new ParkingSpot({
                id: doc.id,
                name: parsed.name,
                electric: parsed.electric,
                imageUrl: parsed.imageUrl ?? undefined,
                imageFilename: parsed.imageFilename ?? undefined,
                attractivity: parsed.attractivity,
                naturalGasAllowed: parsed.naturalGasAllowed,
                isEnabled: parsed.isEnabled,
                inReserve: parsed.inReserve,
                size: parsed.size,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create ParkingSpot ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return parkingSpotSchema.parse({
            name: this.name,
            electric: this.electric,
            imageUrl: this.imageUrl,
            imageFilename: this.imageFilename,
            attractivity: this.attractivity,
            naturalGasAllowed: this.naturalGasAllowed,
            isEnabled: this.isEnabled,
            inReserve: this.inReserve,
            size: this.size,
            timestampCreated: this.timestampCreated ?? Timestamp.now()
        })
    }

    static compare(a: ParkingSpot, b: ParkingSpot) {
        if (a.inReserve && !b.inReserve) return 1
        if (!a.inReserve && b.inReserve) return -1
        if (a.attractivity === b.attractivity) return a.name.localeCompare(b.name)
        return b.attractivity - a.attractivity
    }

    toString(): string {
        return `${this.name} (${this.id})`
    }
}
